import { Fragment, PropsWithChildren } from 'react';
import { InView } from 'react-intersection-observer';

import {
  IconButton,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { RiBookmarkFill, RiDeleteBinLine } from '@remixicon/react';

import { Link } from '@backstage/core-components';

import {
  DateUtils,
  DialogComposite,
  ListEmptyState,
  ResourceNameLink,
  ResourceTableRowMui as ResourceTableRow,
  StickyTableContainerMui as StickyTableContainer,
  StickyTableHeadMui as StickyTableHead,
} from '@spotify/confidence-core-react';
import { useDeleteCollectionEntryMutation } from '@spotify/confidence-graphql';

import {
  CollectionEntryExtensionOptions,
  CollectionEntryOptions,
  findCollectionEntryExtension,
  getExtensionDisplayName,
  useCollectionEntryExtensions,
} from '../../extensions';
import { useCollectionEntries, useDeleteCollectionEntry } from '../../hooks';

const CollectionEntryRow = ({
  resourceName,
  extension,
  children,
}: PropsWithChildren<{
  resourceName: string;
  extension: CollectionEntryExtensionOptions | undefined;
}>) => {
  if (extension?.routeRef) {
    return (
      <ResourceTableRow
        routeRef={extension.routeRef}
        name={resourceName}
        params={extension.getRouteParams?.(resourceName)}
      >
        {children}
      </ResourceTableRow>
    );
  }

  return <TableRow>{children}</TableRow>;
};

const useStyles = makeStyles({
  table: {
    tableLayout: 'fixed',
    '& > tbody > tr > td': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
});

type CollectionEntriesListProps = {
  collectionName: string;
  isFavorites?: boolean;
  metricsUrl?: string;
};

export const CollectionEntriesList = ({
  collectionName,
  isFavorites,
  metricsUrl,
}: CollectionEntriesListProps) => {
  const classes = useStyles();
  const extensions = useCollectionEntryExtensions();

  const {
    collectionEntries,
    isLoading,
    isEmpty,
    error,
    nextPage,
    hasNextPage,
  } = useCollectionEntries({ parent: collectionName });

  const { deleteCollectionEntry, isDeleting } = useDeleteCollectionEntry();
  const [deleteFavoriteEntry] = useDeleteCollectionEntryMutation({
    update: (cache, _, { variables }) => {
      if (variables?.name) {
        cache.evict({
          id: cache.identify({
            __typename: 'CollectionsV1CollectionEntry',
            name: variables.name,
          }),
        });
        cache.gc();
      }
    },
  });

  if (isEmpty) {
    return (
      <ListEmptyState
        title={isFavorites ? 'No favorites yet' : 'No items yet'}
        description={
          isFavorites ? (
            'Bookmark a resource to add it to your favorites.'
          ) : (
            <>
              Browse{' '}
              {metricsUrl ? (
                <MuiLink component={Link} to={metricsUrl} underline="always">
                  metrics
                </MuiLink>
              ) : (
                'metrics'
              )}{' '}
              to add to this collection.
            </>
          )
        }
      />
    );
  }

  return (
    <Fragment>
      <StickyTableContainer
        loading={isLoading && !collectionEntries?.length}
        error={error}
      >
        <Table className={classes.table}>
          <StickyTableHead>
            <TableRow>
              {isFavorites && <TableCell width={48} padding="checkbox" />}
              <TableCell>Name</TableCell>
              <TableCell width="15%">Type</TableCell>
              <TableCell width="20%">Added</TableCell>
              {!isFavorites && <TableCell width={48} padding="checkbox" />}
            </TableRow>
          </StickyTableHead>
          <TableBody>
            {collectionEntries?.map(entry => {
              const extension = findCollectionEntryExtension(extensions, entry);
              const resource = entry.resource as
                | CollectionEntryOptions['resource']
                | undefined;
              const displayName = resource
                ? getExtensionDisplayName(extension, resource, entry)
                : entry.name;
              const ResourceTypeLabel = extension?.ResourceTypeLabel;
              const resourceType = extension?.resourceType ?? 'Unknown';
              const resourceName =
                resource && 'name' in resource
                  ? String(resource.name)
                  : entry.name;

              const cells = (
                <>
                  {isFavorites && (
                    <TableCell
                      padding="checkbox"
                      onClick={e => e.stopPropagation()}
                    >
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() =>
                          deleteFavoriteEntry({
                            variables: { name: entry.name },
                          })
                        }
                      >
                        <RiBookmarkFill size={18} />
                      </IconButton>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body1">
                      <ResourceNameLink
                        name={resourceName}
                        text={displayName}
                      />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" color="textSecondary">
                      {ResourceTypeLabel && resource ? (
                        <ResourceTypeLabel resource={resource} />
                      ) : (
                        resourceType
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" color="textSecondary">
                      {entry.createTime
                        ? DateUtils.xAgo(new Date(entry.createTime))
                        : '-'}
                    </Typography>
                  </TableCell>
                  {!isFavorites && (
                    <TableCell
                      padding="checkbox"
                      onClick={e => e.stopPropagation()}
                    >
                      <DialogComposite.Root>
                        <DialogComposite.Trigger>
                          <IconButton size="small" disabled={isDeleting}>
                            <RiDeleteBinLine size={18} />
                          </IconButton>
                        </DialogComposite.Trigger>
                        <DialogComposite.Content
                          component="form"
                          onSubmit={() => deleteCollectionEntry(entry.name)}
                          data-track-scope="dialog.collection-entry"
                          data-track-id="dialog.collection-entry"
                          data-track-impression
                        >
                          <DialogComposite.Header>
                            <DialogComposite.Title>
                              Remove from collection
                            </DialogComposite.Title>
                            <DialogComposite.CloseButton />
                          </DialogComposite.Header>
                          <DialogComposite.Body>
                            <Typography>
                              Are you sure you want to remove{' '}
                              <strong>{displayName}</strong> from this
                              collection?
                            </Typography>
                          </DialogComposite.Body>
                          <DialogComposite.Actions>
                            <DialogComposite.Actions.Cancel>
                              Cancel
                            </DialogComposite.Actions.Cancel>
                            <DialogComposite.Actions.Submit>
                              Remove
                            </DialogComposite.Actions.Submit>
                          </DialogComposite.Actions>
                        </DialogComposite.Content>
                      </DialogComposite.Root>
                    </TableCell>
                  )}
                </>
              );

              return (
                <CollectionEntryRow
                  key={entry.name}
                  resourceName={resourceName}
                  extension={extension}
                >
                  {cells}
                </CollectionEntryRow>
              );
            })}
          </TableBody>
        </Table>
      </StickyTableContainer>
      {hasNextPage && (
        <InView
          onChange={async inView => {
            if (inView) await nextPage();
          }}
        />
      )}
    </Fragment>
  );
};
